import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Panel, Stat } from "@/components/Panel";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { RefreshCw, Globe, Eye, Users, ArrowUpRight, MapPin, ExternalLink, Send, Flame, Wrench } from "lucide-react";
import ReferrerSessionsDrawer from "@/components/ReferrerSessionsDrawer";
import CountrySessionsDrawer from "@/components/CountrySessionsDrawer";
import HotSessionsPanel from "@/components/HotSessionsPanel";
import CorporateWarmLeadsPanel from "@/components/CorporateWarmLeadsPanel";
import WatchlistManager from "@/components/WatchlistManager";

// Phase 36.13 — Native pageview analytics dashboard.
const RANGES = [
  { key: 1, label: "24H" },
  { key: 7, label: "7D" },
  { key: 30, label: "30D" },
  { key: 90, label: "90D" },
];

export default function AdminVisitors() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [includeInternal, setIncludeInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [drillReferrer, setDrillReferrer] = useState(null);
  const [drillCountry, setDrillCountry] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const runBackfillBots = async () => {
    if (!confirm(
      "Retroactively mark historical pageviews from AWS / GCP / Azure / DigitalOcean / etc. as bots? " +
      "This cleans up the cloud-egress visits that were stored before the Phase 25.13ah filter shipped. " +
      "Safe — idempotent. Run again any time to catch new historical data."
    )) return;
    setBackfilling(true);
    try {
      const r = await api.post("/admin/visitors/backfill-cloud-egress");
      const breakdown = (r.data.breakdown || []).slice(0, 5)
        .map(b => `${b.count}× ${b.org}`).join(" · ");
      toast.success(
        `Flagged ${r.data.flagged}/${r.data.scanned} as cloud-egress bots${breakdown ? `\n${breakdown}` : ""}`,
        { duration: 8000 },
      );
      // Force-refresh the page after a beat so the panels reflect new is_bot=True rows
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const sendHotDigest = async () => {
    if (!confirm("Email the Daily Hot Sessions digest to your super-admin inbox now? (force-sends even if no HOT/WARM sessions)")) return;
    setSendingDigest(true);
    try {
      const r = await api.post("/admin/hot-sessions-digest/send-now");
      if (r.data.ok) {
        const p = r.data.payload;
        toast.success(
          `Hot Sessions digest sent to ${r.data.sent_to} (${p.hot_count} HOT · ${p.warm_count} WARM)`,
          { duration: 6000 },
        );
      } else {
        toast.error(`Digest skipped: ${r.data.reason}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSendingDigest(false);
    }
  };

  const sendPulse = async () => {
    if (!confirm("Send the weekly Sales Pulse email to the super-admin inbox now?")) return;
    setSending(true);
    try {
      const r = await api.post("/admin/sales-pulse/send-now");
      if (r.data.ok) {
        toast.success(`Pulse sent to ${r.data.sent_to}`, { duration: 6000 });
      } else {
        toast.error(`Pulse skipped: ${r.data.reason}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const load = async (d = days, inc = includeInternal) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/visitors?days=${d}&include_internal=${inc ? "true" : "false"}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load visitor data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days, includeInternal); }, [days, includeInternal]);

  const chartData = useMemo(() => (data?.daily || []).map(d => ({
    day: d.day.slice(5),  // MM-DD
    views: d.views,
  })), [data]);

  const conv = useMemo(() => {
    if (!data) return null;
    const totalLeads = (data.leads?.industry_leads || 0)
      + (data.leads?.founder_guide_leads || 0)
      + (data.leads?.industrial_waitlist || 0);
    const visitors = data.unique_visitors || 0;
    // Conversion is meaningless until you have at least 10 visitors in the window.
    const meaningful = visitors >= 10;
    const rate = meaningful ? ((totalLeads / visitors) * 100).toFixed(1) : null;
    return { totalLeads, rate, visitors, meaningful };
  }, [data]);

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="admin-visitors-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// PUBLIC SURFACE · ANONYMOUS PAGEVIEW TELEMETRY</div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-1">Visitors</h1>
          <p className="text-xs text-zinc-500 max-w-xl mt-1.5">
            Privacy-first first-party analytics — no cookies, no third-party calls. IP hashed daily, 90-day retention. Captures every public-page view.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1" data-testid="visitor-range-tabs">
            {RANGES.map(r => (
              <button
                key={r.key}
                onClick={() => setDays(r.key)}
                data-testid={`visitor-range-${r.key}`}
                className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest border ${
                  days === r.key
                    ? "border-[#FFB000] text-[#FFB000] bg-[#FFB000]/10"
                    : "border-zinc-800 text-zinc-500 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setIncludeInternal((v) => !v)}
            data-testid="visitor-toggle-internal"
            title={includeInternal
              ? "Currently showing your own admin + Emergent platform traffic. Click to filter it out."
              : "Currently hiding your own admin + Emergent platform traffic. Click to include it."}
            className={`border px-3 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center gap-2 ${
              includeInternal
                ? "border-[#FFB000] text-[#FFB000] bg-[#FFB000]/10"
                : "border-zinc-800 text-zinc-500 hover:text-white"
            }`}
          >
            {includeInternal ? "INTERNAL: ON" : "INTERNAL: OFF"}
          </button>
          <button
            onClick={() => load(days)}
            disabled={loading}
            data-testid="visitor-refresh"
            className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300 flex items-center gap-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={sendPulse}
            disabled={sending}
            data-testid="visitor-send-pulse"
            title="Email the weekly Sales Pulse digest to your super-admin inbox now"
            className="border border-[#FFB000]/40 hover:bg-[#FFB000]/10 text-[#FFB000] px-3 py-2 font-mono text-[11px] uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            <Send className={`w-3.5 h-3.5 ${sending ? "animate-pulse" : ""}`} />
            {sending ? "SENDING…" : "EMAIL PULSE"}
          </button>
          <button
            onClick={sendHotDigest}
            disabled={sendingDigest}
            data-testid="visitor-send-hot-digest"
            title="Email the Daily Hot Sessions digest to your super-admin inbox now (also runs automatically at 22:00 UTC daily)"
            className="border border-[#FF6B5C]/40 hover:bg-[#FF6B5C]/10 text-[#FF6B5C] px-3 py-2 font-mono text-[11px] uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            <Flame className={`w-3.5 h-3.5 ${sendingDigest ? "animate-pulse" : ""}`} />
            {sendingDigest ? "SENDING…" : "EMAIL HOT DIGEST"}
          </button>
          <button
            onClick={runBackfillBots}
            disabled={backfilling}
            data-testid="visitor-backfill-bots"
            title="Retroactively flag historical AWS / GCP / Azure / DigitalOcean visits as bots. Idempotent — safe to run multiple times."
            className="border border-zinc-700 hover:border-[#FFB000]/50 hover:bg-zinc-950 text-zinc-400 hover:text-[#FFB000] px-3 py-2 font-mono text-[11px] uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            <Wrench className={`w-3.5 h-3.5 ${backfilling ? "animate-spin" : ""}`} />
            {backfilling ? "BACKFILLING…" : "CLEAN BOTS"}
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
        <Stat testId="visitor-total-pv" label="TOTAL PAGEVIEWS" value={data?.total_pageviews ?? "—"} accent="white" sub={pvFilterSummary(data)} />
        <Stat testId="visitor-unique" label="UNIQUE VISITORS" value={data?.unique_visitors ?? "—"} accent="amber" sub={`(${data?.unique_sessions ?? 0} SESSIONS)`} />
        <Stat testId="visitor-leads" label="LEADS CAPTURED" value={conv?.totalLeads ?? "—"} accent="green" sub={`${data?.leads?.new_signups ?? 0} NEW WORKSPACES`} />
        <Stat testId="visitor-conv-rate" label="CONVERSION" value={conv?.meaningful ? `${conv.rate}%` : "—"} accent="amber" sub={conv?.meaningful ? `${conv.totalLeads} / ${conv.visitors}` : "PENDING · NEED 10+ VISITORS"} />
      </div>

      {/* Daily chart */}
      <Panel title="// PAGEVIEWS · DAILY" testId="visitor-chart">
        <div className="p-4 h-[260px]">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontFamily: "monospace", fontSize: 10, fill: "#71717a" }} />
                <YAxis tick={{ fontFamily: "monospace", fontSize: 10, fill: "#71717a" }} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", fontFamily: "monospace", fontSize: 11 }}
                  cursor={{ fill: "rgba(255,176,0,0.05)" }}
                />
                <Bar dataKey="views" fill="#FFB000" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center font-mono text-xs text-zinc-600 py-12">
              No pageviews yet in this window. Visit a public page (e.g. /industries/mining) to register the first one.
            </div>
          )}
        </div>
      </Panel>

      <CorporateWarmLeadsPanel days={days} />

      <HotSessionsPanel days={days} includeInternal={includeInternal} />

      <WatchlistManager />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ListPanel
          title="// TOP PAGES"
          icon={Eye}
          items={(data?.top_paths || []).map(p => ({
            primary: p.path,
            secondary: `${p.views} views · ${p.unique} unique`,
            metric: p.views,
            link: p.path.startsWith("/") ? p.path : null,
          }))}
          testId="visitor-top-paths"
          emptyHint="No public pageviews yet."
        />
        <ListPanel
          title="// TOP REFERRERS"
          icon={ArrowUpRight}
          items={(data?.top_referrers || []).map(r => ({
            primary: r.referrer,
            secondary: r.wow_delta != null && r.wow_delta !== 0
              ? `WoW ${r.wow_delta > 0 ? "+" : ""}${r.wow_delta}`
              : "",
            metric: r.views,
            // (direct)/(internal) have no inbound referrer to drill into.
            onClick: r.referrer && r.referrer !== "(direct)" && r.referrer !== "(internal)"
              ? () => setDrillReferrer(r.referrer)
              : undefined,
          }))}
          testId="visitor-referrers"
          emptyHint="No external referrers tracked."
        />
        <ListPanel
          title="// UTM SOURCES"
          icon={ExternalLink}
          items={(data?.top_utm_sources || []).map(u => ({
            primary: u.utm_source,
            secondary: "",
            metric: u.views,
          }))}
          testId="visitor-utm"
          emptyHint="No campaign-tagged traffic yet. Add ?utm_source=linkedin to your shared links."
        />
        <ListPanel
          title="// COUNTRIES"
          icon={MapPin}
          items={(data?.top_countries || []).map(c => ({
            primary: c.country,
            secondary: "",
            metric: c.views,
            onClick: c.country && c.country !== "(unknown)"
              ? () => setDrillCountry(c.country)
              : undefined,
          }))}
          testId="visitor-countries"
          emptyHint="Country data shows when you're on a CDN that injects the CF-IPCountry header."
        />
      </div>

      <Panel title="// LEAD CONVERSION FUNNEL" testId="visitor-funnel">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-px bg-zinc-900">
          <Stat label="VISITORS" value={data?.unique_visitors ?? 0} accent="white" />
          <Stat label="INDUSTRY LEADS" value={data?.leads?.industry_leads ?? 0} accent="amber" />
          <Stat label="FG DOWNLOADS" value={data?.leads?.founder_guide_leads ?? 0} accent="amber" />
          <Stat label="NEW WORKSPACES" value={data?.leads?.new_signups ?? 0} accent="green" />
        </div>
      </Panel>

      <ReferrerSessionsDrawer
        referrer={drillReferrer}
        days={days}
        includeInternal={includeInternal}
        onClose={() => setDrillReferrer(null)}
      />

      <CountrySessionsDrawer
        country={drillCountry}
        days={days}
        includeInternal={includeInternal}
        onClose={() => setDrillCountry(null)}
      />
    </div>
  );
}

function pvFilterSummary(data) {
  if (!data) return undefined;
  const parts = [];
  if (data.bot_pageviews) parts.push(`${data.bot_pageviews} bot`);
  if (data.internal_pageviews && !data.include_internal) parts.push(`${data.internal_pageviews} internal`);
  if (!parts.length) return undefined;
  return `+ ${parts.join(" · ")} FILTERED`.toUpperCase();
}


function ListPanel({ title, icon: Icon, items, testId, emptyHint }) {
  return (
    <Panel title={title} testId={testId}>
      <div className="divide-y divide-zinc-900">
        {items.length === 0 && (
          <div className="p-6 text-center font-mono text-xs text-zinc-600 leading-relaxed">{emptyHint}</div>
        )}
        {items.slice(0, 12).map((it, i) => {
          const inner = (
            <>
              <div className="flex items-center gap-2 min-w-0">
                <Icon className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" />
                {it.link ? (
                  <a href={it.link} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] text-zinc-300 truncate hover:text-[#FFB000]">{it.primary}</a>
                ) : (
                  <span className="font-mono text-[11px] text-zinc-300 truncate">{it.primary || "(none)"}</span>
                )}
                {it.secondary && <span className="font-mono text-[10px] text-zinc-600">{it.secondary}</span>}
              </div>
              <span className="font-mono text-[11px] text-[#FFB000] flex-shrink-0">{it.metric}</span>
            </>
          );
          if (it.onClick) {
            return (
              <button
                key={i}
                type="button"
                onClick={it.onClick}
                data-testid={`${testId}-row-${i}`}
                className="w-full text-left flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-zinc-950/30 transition-colors"
              >
                {inner}
              </button>
            );
          }
          return (
            <div key={i} className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-zinc-950/30">
              {inner}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
